import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FaCheck, FaChevronRight, FaSearch } from 'react-icons/fa';

export default function SearchableDropdown({
  options = [],
  selectedValue,
  onSelect,
  placeholder,
  disabled,
  displayKey = 'name',
  valueKey = 'id',
  isLoading = false,
  emptyMessage = 'No results found',
  styles = {}
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const dropdownRef = useRef(null);
  const dropdownId = useId();

  const filtered = useMemo(() => {
    const query = searchInput.toLowerCase();

    return options.filter((option) => {
      const displayValue = option?.[displayKey];

      return String(displayValue ?? '').toLowerCase().includes(query);
    });
  }, [displayKey, options, searchInput]);

  const selectedOption = options.find((option) => String(option?.[valueKey]) === String(selectedValue));
  const menuId = `searchable-dropdown-menu-${dropdownId}`;

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (value) => {
    setIsOpen(false);
    setSearchInput('');
    onSelect?.(value);
  };

  return (
    <div ref={dropdownRef} style={styles.container}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        style={{
          ...styles.trigger,
          ...(isOpen ? styles.openTrigger : null),
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        <div style={styles.triggerText}>
          {isLoading ? 'Loading...' : selectedOption ? selectedOption[displayKey] : placeholder}
        </div>
        <FaChevronRight
          style={{
            fontSize: 12,
            transition: 'transform 0.3s ease',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            ...styles.indicator
          }}
        />
      </button>

      {isOpen && !disabled && (
        <div id={menuId} role="listbox" style={styles.menu}>
          <div style={styles.searchContainer}>
            <FaSearch style={styles.searchIcon} />
            <input
              autoFocus
              type="text"
              aria-label={`${placeholder || 'Dropdown'} search`}
              placeholder="Search..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              style={styles.searchInput}
            />
          </div>

          <div style={styles.list}>
            {filtered.length === 0 ? (
              <div style={styles.emptyOption}>{emptyMessage}</div>
            ) : (
              filtered.map((option) => {
                const isSelected = String(selectedValue) === String(option?.[valueKey]);

                return (
                  <button
                    type="button"
                    key={option?.[valueKey]}
                    onClick={() => handleSelect(option?.[valueKey])}
                    role="option"
                    aria-selected={isSelected}
                    style={{
                      ...styles.option,
                      background: isSelected ? '#ede9fe' : 'transparent',
                      color: isSelected ? '#6366f1' : '#1e293b'
                    }}
                  >
                    <div style={styles.optionText}>
                      <div style={styles.optionName}>{option?.[displayKey]}</div>
                      <div style={styles.optionId}>ID: {option?.[valueKey]}</div>
                    </div>
                    {isSelected && <FaCheck style={{ fontSize: 12, color: '#6366f1' }} />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}